import { reactErrorHandler } from '@sentry/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { buildVersion } from '@/core/build-version';
import { isChunkLoadError, reloadForNewBuild } from '@/core/chunk-reload';
import '@/styles/globals.css';

import('@/core/http/preconnect-account-api').then(
  ({ preconnectAccountApi }) => preconnectAccountApi(),
  () => {
    // The app import below fails the same way, and says so.
  }
);

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Missing #root element');
}

// A page restored from the back/forward cache would show the previous session, signing key included.
window.addEventListener('pageshow', event => {
  if (event.persisted) {
    window.location.reload();
  }
});

// Errors caught by route boundaries are reported by RouteErrorBoundary, which skips 401/403/404.
const root = createRoot(rootElement, {
  onUncaughtError: reactErrorHandler(),
  onRecoverableError: reactErrorHandler()
});

try {
  const { initSentry } = await import('@/app/sentry');
  // Sentry must be initialised before App creates the router, or routing is not instrumented.
  initSentry();
  const { App } = await import('@/app/App');
  root.render(
    <StrictMode>
      <App />
    </StrictMode>
  );
} catch (error) {
  if (!(isChunkLoadError(error) && reloadForNewBuild())) {
    root.render(
      <p className="p-6 font-mono text-destructive text-sm">
        {error instanceof Error ? error.message : 'Unable to start Account'}
      </p>
    );
    throw error;
  }
}

console.group('🚀 Deployment info');
console.info('Mode:', import.meta.env.MODE);
console.info('SHA:', buildVersion());
console.groupEnd();
